import { Injectable, inject, computed } from '@angular/core';
import { Observable, catchError, throwError, tap, map, of } from 'rxjs';
import { UniversalService, UniversalServiceConfig } from './universal.service';
import { RolesService } from '../roles.service';
import { IShiftResponse, IShiftPaginationResponse, ICreateShiftDto, IUpdateShiftDto, IShiftFilters, ICurrentShift, ShiftFunction } from '../../interfaces';
import { Roles } from '../../interfaces/user.interface';
import { SHIFT_ENDPOINTS, SHIFT_CACHE_KEYS } from '../../constants';

/**
 * Configuração do UniversalService para Shifts
 * Seguindo exatamente a mesma lógica do ShiftsController do backend
 */
const SHIFT_CONFIG: UniversalServiceConfig<IShiftResponse, ICreateShiftDto, IUpdateShiftDto, IShiftFilters> = {
    entityName: 'shifts',
    endpoints: SHIFT_ENDPOINTS,
    cacheKeys: SHIFT_CACHE_KEYS,
    defaultCacheTtl: 30000, // 30 segundos
    checkPermissions: (dependencies: { rolesService: RolesService }) => {
        return dependencies.rolesService.hasAnyRole([
            Roles.SYSTEM_ADMIN,
            Roles.ADMIN,
            Roles.GUARD,
            Roles.SUPERVISOR,
            Roles.HR,
            Roles.DOORMAN,
            Roles.JARDINER,
            Roles.MAINTENANCE_ASSISTANT,
            Roles.MONITORING_OPERATOR,
            Roles.ADMINISTRATIVE_ASSISTANT,
        ]);
    },
    transformData: (apiShift: IShiftResponse) => {
        // Função segura para converter datas
        const safeDate = (date: string | Date | undefined): Date | null => {
            if (!date) return null;
            const parsedDate = new Date(date);
            return isNaN(parsedDate.getTime()) ? null : parsedDate;
        };

        // Função segura para formatar hora
        const hourAndMinute = (date: string | Date | undefined): string => {
            const validDate = safeDate(date);
            return validDate ? validDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--';
        };

        return {
            ...apiShift,
            date: apiShift.startTime,
            startTime: hourAndMinute(apiShift.startTime),
            breakStartTime: hourAndMinute(apiShift.breakStartTime),
            breakEndTime: hourAndMinute(apiShift.breakEndTime),
            endTime: hourAndMinute(apiShift.endTime),
            status: mapApiStatusToShiftDataStatus(apiShift.status),
            function: mapShiftFunctionToString(apiShift.function),
        } as any; // Usar 'any' para permitir propriedades adicionais
    },
};

/**
 * Serviço para gerenciamento de turnos
 * Seguindo exatamente a mesma lógica do ShiftsController do backend
 * Usando UniversalService como base para operações CRUD
 */
@Injectable({ providedIn: 'root' })
export class ShiftService extends UniversalService<IShiftResponse, ICreateShiftDto, IUpdateShiftDto, IShiftFilters> {
    private readonly rolesService = inject(RolesService);

    constructor() {
        super(SHIFT_CONFIG);
    }

    /**
     * Sobrescreve o método para fornecer as dependências de permissões
     */
    protected override getPermissionsDependencies(): { rolesService: RolesService } {
        return { rolesService: this.rolesService };
    }

    // Computed signals específicos para turnos
    readonly canManageShifts = computed(() =>
        this.rolesService.hasAnyRole([
            Roles.SYSTEM_ADMIN,
            Roles.ADMIN,
            Roles.GUARD,
            Roles.SUPERVISOR,
            Roles.HR,
            Roles.DOORMAN,
            Roles.JARDINER,
            Roles.MAINTENANCE_ASSISTANT,
            Roles.MONITORING_OPERATOR,
            Roles.ADMINISTRATIVE_ASSISTANT,
        ])
    );
    readonly canStartShift = computed(() =>
        this.rolesService.hasAnyRole([
            Roles.SYSTEM_ADMIN,
            Roles.ADMIN,
            Roles.SUPERVISOR,
            Roles.GUARD,
            Roles.DOORMAN,
            Roles.JARDINER,
            Roles.MAINTENANCE_ASSISTANT,
            Roles.MONITORING_OPERATOR,
            Roles.ADMINISTRATIVE_ASSISTANT,
        ])
    );
    readonly canEndShift = computed(() =>
        this.rolesService.hasAnyRole([
            Roles.SYSTEM_ADMIN,
            Roles.ADMIN,
            Roles.SUPERVISOR,
            Roles.GUARD,
            Roles.DOORMAN,
            Roles.JARDINER,
            Roles.MAINTENANCE_ASSISTANT,
            Roles.MONITORING_OPERATOR,
            Roles.ADMINISTRATIVE_ASSISTANT,
        ])
    );

    // ============================================================================
    // 📋 MÉTODOS ESPECÍFICOS DO SHIFTSCONTROLLER (não estão no UniversalController)
    // ============================================================================
    /**
     * Obtém o turno atual garantido (aguarda busca se necessário)
     * @returns Observable que sempre retorna o turno atual ou null
     */
    getCurrentShift(): Observable<IShiftResponse | null> {
        const currentItem = this.currentItem();
        // Se já tem item atual, retorna imediatamente
        if (currentItem) {
            return of(currentItem);
        }

        // Se não tem, busca o turno em andamento e aguarda
        return this.buscarEmAndamento();
    }

    /**
     * Carrega e define o turno atual (método simples)
     */
    loadCurrentShift(): void {
        const currentShift = this.getCurrent();

        if (currentShift) {
            return;
        }

        this.buscarEmAndamento().subscribe({
            next: shift => {
                if (shift) {
                    this.setCurrentItem(shift as any);
                }
            },
            error: error => {
                console.error('Erro ao carregar turno atual:', error);
            },
        });
    }

    /**
     * Busca turno em andamento
     * @returns Observable com turno em andamento
     */
    buscarEmAndamento(): Observable<IShiftResponse | null> {
        return this.apiService
            .get<IShiftResponse | null>(SHIFT_ENDPOINTS.IN_PROGRESS, {
                useCache: false,
                cacheKey: SHIFT_CACHE_KEYS.IN_PROGRESS,
                cacheTtl: 60000, // 1 minuto para dados em tempo real
            })
            .pipe(
                map(shift => {
                    const data = (shift as any).data;
                    return data;
                }),
                tap(shift => {
                    if (shift) {
                        this.setCurrentItem(shift);
                    }
                }),
                catchError(error => {
                    console.error('Erro ao buscar turno em andamento:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Busca turnos por usuário
     * @param userId ID do usuário
     * @param filters Filtros opcionais
     * @returns Observable com turnos do usuário
     */
    buscarPorUsuario(userId: string, filters: IShiftFilters = {}): Observable<IShiftPaginationResponse> {
        const params: Record<string, any> = { ...filters };

        return this.apiService
            .get<IShiftPaginationResponse>(SHIFT_ENDPOINTS.BY_USER(userId), {
                params,
                useCache: true,
                cacheKey: SHIFT_CACHE_KEYS.BY_USER(userId) + JSON.stringify(filters),
                cacheTtl: 300000, // 5 minutos
            })
            .pipe(
                tap(response => {
                    if (response.data && response.data.length > 0) {
                        this.itemsCache.set(response.data);
                    }
                }),
                catchError(error => {
                    console.error(`Erro ao buscar turnos do usuário ${userId}:`, error);
                    return throwError(() => error);
                })
            );
    }

    // ============================================================================
    // 🕐 MÉTODOS DE CONTROLE DE TURNO (específicos do ShiftsController)
    // ============================================================================

    /**
     * Inicia um novo turno
     * @param dto Dados para início do turno
     * @returns Observable com turno iniciado
     */
    inicioDoTurno(dto: ICreateShiftDto): Observable<IShiftResponse> {
        if (!this.canStartShift()) {
            return throwError(() => new Error('Apenas guardas podem iniciar turnos'));
        }

        return this.apiService
            .post<IShiftResponse>(SHIFT_ENDPOINTS.START_TIME, dto, {
                useCache: false,
                showLoader: true,
            })
            .pipe(
                tap(shift => {
                    this.setCurrentItem(shift);
                    this.updateCacheWithItem(shift);
                    this.invalidateCache();
                }),
                catchError(error => {
                    console.error('Erro ao iniciar turno:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Inicia intervalo do turno
     * @param id ID do turno
     * @param dto Dados para início do intervalo
     * @returns Observable com turno atualizado
     */
    inicioDoIntervalo(id: string, dto: IUpdateShiftDto): Observable<IShiftResponse> {
        if (!this.canManageShifts()) {
            return throwError(() => new Error('Permissão insuficiente para gerenciar turnos'));
        }

        return this.apiService
            .patch<IShiftResponse>(SHIFT_ENDPOINTS.BREAK_START_TIME(id), dto, {
                useCache: false,
                showLoader: true,
            })
            .pipe(
                tap(shift => {
                    this.setCurrentItem(shift);
                    this.updateCacheWithItem(shift);
                    this.invalidateCache();
                }),
                catchError(error => {
                    console.error(`Erro ao iniciar intervalo do turno ${id}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Finaliza intervalo do turno
     * @param id ID do turno
     * @param dto Dados para fim do intervalo
     * @returns Observable com turno atualizado
     */
    fimDoIntervalo(id: string, dto: IUpdateShiftDto): Observable<IShiftResponse> {
        if (!this.canManageShifts()) {
            return throwError(() => new Error('Permissão insuficiente para gerenciar turnos'));
        }

        return this.apiService
            .patch<IShiftResponse>(SHIFT_ENDPOINTS.BREAK_END_TIME(id), dto, {
                useCache: false,
                showLoader: true,
            })
            .pipe(
                tap(shift => {
                    this.setCurrentItem(shift);
                    this.updateCacheWithItem(shift);
                    this.invalidateCache();
                }),
                catchError(error => {
                    console.error(`Erro ao finalizar intervalo do turno ${id}:`, error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Finaliza turno
     * @param id ID do turno
     * @param dto Dados para fim do turno
     * @returns Observable com turno finalizado
     */
    fimDoTurno(id: string, dto: IUpdateShiftDto): Observable<IShiftResponse> {
        if (!this.canEndShift()) {
            return throwError(() => new Error('Apenas guardas podem finalizar turnos'));
        }

        return this.apiService
            .patch<IShiftResponse>(SHIFT_ENDPOINTS.END_TIME(id), dto, {
                useCache: false,
                showLoader: true,
            })
            .pipe(
                tap(shift => {
                    this.clearCache();
                    this.clearCurrentItem();
                    this.invalidateCache();
                }),
                catchError(error => {
                    console.error(`Erro ao finalizar turno ${id}:`, error);
                    return throwError(() => error);
                })
            );
    }
}

export function mapApiStatusToShiftDataStatus(apiStatus: string): 'Completo' | 'Em andamento' | 'Intervalo' | 'Pendente' | 'Ausência' {
    switch (apiStatus) {
        case 'COMPLETED':
            return 'Completo';
        case 'IN_PROGRESS':
            return 'Em andamento';
        case 'BREAK':
            return 'Intervalo';
        case 'PENDING':
            return 'Pendente';
        case 'ABSENCE':
            return 'Ausência';
        default:
            return 'Pendente';
    }
}

export function mapShiftFunctionToString(functionType: ShiftFunction): string {
    switch (functionType) {
        case ShiftFunction.PATROL:
            return 'Vigilante Ronda';
        case ShiftFunction.SUPPORT:
            return 'Vigilante Apoio';
        case ShiftFunction.DOORMAN:
            return 'Portaria';
        case ShiftFunction.ADMIN:
            return 'Administrador';
        case ShiftFunction.HR:
            return 'Recursos Humanos';
        case ShiftFunction.SUPERVISOR:
            return 'Supervisor';
        case ShiftFunction.POST_SUPERVISOR:
            return 'Supervisor de Posto';
        case ShiftFunction.POST_RESIDENT:
            return 'Morador/Residente';
        case ShiftFunction.JARDINER:
            return 'Jardineiro';
        case ShiftFunction.MAINTENANCE_ASSISTANT:
            return 'Auxiliar de Manutenção';
        case ShiftFunction.MONITORING_OPERATOR:
            return 'Operador de Monitoramento';
        case ShiftFunction.ADMINISTRATIVE_ASSISTANT:
            return 'Assistente Administrativo';
        default:
            return functionType;
    }
}
